import type { AdjustDirection } from "../types/cardWallets.types";

/**
 * An adjust whose answer never arrived. Written BEFORE the request leaves and removed only once the outcome
 * is known, so a reload, a closed dialog or a navigation all resume it.
 *
 * It needs no extra endpoint to settle: `external_reference` is both the deduplication key and recoverable
 * from the history, because the movement's `provider_transaction_id` is derived from it.
 */
export interface AdjustAttempt {
  reference: string;
  direction: AdjustDirection;
  /** Unsigned decimal string, as typed. */
  magnitude: string;
}

type AttemptStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const STORAGE_KEY = "coloca.cardWalletAdjust.unresolved";

const isAttempt = (value: unknown): value is AdjustAttempt => {
  const attempt = value as Partial<AdjustAttempt> | null;
  return (
    !!attempt &&
    typeof attempt.reference === "string" &&
    attempt.reference.length > 0 &&
    (attempt.direction === "credit" || attempt.direction === "debit") &&
    typeof attempt.magnitude === "string"
  );
};

/** Keyed by wallet, because the reference is unique per wallet and nothing else. */
export function createAdjustAttemptStore(storage: AttemptStorage | null) {
  const memory = new Map<string, AdjustAttempt>();

  const readAll = (): Record<string, unknown> => {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  };

  const writeAll = (all: Record<string, unknown>) => {
    try {
      if (Object.keys(all).length) storage?.setItem(STORAGE_KEY, JSON.stringify(all));
      else storage?.removeItem(STORAGE_KEY);
    } catch {
      return;
    }
  };

  return {
    get(walletId: string): AdjustAttempt | null {
      const stored = readAll()[walletId];
      return isAttempt(stored) ? stored : (memory.get(walletId) ?? null);
    },
    set(walletId: string, attempt: AdjustAttempt | null): void {
      const all = readAll();
      if (attempt) {
        memory.set(walletId, attempt);
        all[walletId] = attempt;
      } else {
        memory.delete(walletId);
        delete all[walletId];
      }
      writeAll(all);
    },
  };
}

const browserStorage = (): AttemptStorage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const unresolvedAdjustAttempts = createAdjustAttemptStore(browserStorage());
